import { useNavigate } from 'react-router-dom'
import { useAccountStore } from '@/stores/account-store'
import { PostLoginNavigate } from '../components/post-login-navigate'
import { subscriptionState } from '../subscription-state'

/**
 * Shown in beta mode to a signed-in user without a live subscription (their $0 comp
 * sub was never provisioned, or the nightly reconcile canceled it because their prod
 * subscription lapsed). Beta has no billing flow, so there's nothing to buy here —
 * this is the terminal "no access" page instead of /billing.
 *
 * It re-checks entitlement on mount so a user whose access came back (prod sub
 * reactivated → `_maybe_regrant_beta` re-mints the comp sub) isn't stranded here.
 * Without that, the route has no guard at all and there is no way off the page.
 * The conditions mirror AuthGuard's and are its exact inverse, so the two can't
 * bounce against each other: AuthGuard sends you here only when state === 'none'.
 */
export function BetaLockedPage() {
  const user = useAccountStore((s) => s.user)
  const navigate = useNavigate()

  // Only redirect on a positive entitlement signal. While the user is still loading
  // (null), stay put — bouncing on absent data would be a flicker, or a false exit.
  const entitled = !!user && (user.betaAccess === true || subscriptionState(user) === 'active')
  if (entitled) return <PostLoginNavigate />

  // The locked user is still authenticated, so a plain link to /login would bounce
  // (login redirects a live session back to /dashboard → AuthGuard → here). Sign out
  // first so /login is reachable to sign in as someone else.
  const handleSignOut = async () => {
    await useAccountStore.getState().logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Beta access required</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The EDGE beta preview is available to members with an active EDGE subscription. If you
            believe you should have access, please reach out to the team.
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          <button type="button" onClick={handleSignOut} className="text-primary hover:underline">
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  )
}
